import React from 'react';
import { X, CheckCircle2, AlertCircle, Calendar, Clock, User, Phone, Briefcase, MapPin, Loader2 } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';

export default function BookingConfirmModal({
  isOpen,
  onClose,
  onConfirm,
  loading = false,
  bookingDetails = {},
}) {
  const { t, currentLang } = useLanguage();

  if (!isOpen) return null;

  const {
    serviceTitle = '',
    lawyerName = '',
    officeName = 'Jizzax ofisi',
    date = '',
    time = '',
    clientName = '',
    clientPhone = '',
    clientEmail = '',
    clientRegion = '',
    clientDistrict = '',
    clientReason = '',
  } = bookingDetails;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 overflow-y-auto">
      {/* Dark Backdrop */}
      <div 
        className="fixed inset-0 bg-black/85 backdrop-blur-md transition-opacity animate-in fade-in duration-200"
        onClick={onClose}
      />

      {/* Modal Dialog (Scaled to fit all screen sizes with visible close button) */}
      <div className="relative w-full max-w-lg bg-[#070c16] border border-[#c59b27]/50 rounded-2xl shadow-2xl shadow-black/90 overflow-hidden z-10 my-auto max-h-[92vh] flex flex-col animate-in zoom-in-95 duration-200">
        
        {/* Top Gold Bar */}
        <div className="h-1.5 w-full bg-gradient-to-r from-[#b38038] via-[#c59b27] to-[#d4af37] shrink-0" />

        {/* Close Button (X) - Prominent, high-contrast, always visible */}
        <button
          type="button"
          onClick={onClose}
          disabled={loading}
          aria-label="Yopish"
          className="absolute top-3.5 right-3.5 z-30 w-8 h-8 rounded-full bg-[#131d2e] hover:bg-[#1c2c46] border border-[#c59b27]/40 hover:border-[#c59b27] flex items-center justify-center text-slate-300 hover:text-white transition-all cursor-pointer disabled:opacity-50 shadow-md"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="p-5 sm:p-6 overflow-y-auto">
          
          <div className="text-center mb-5">
            <div className="w-11 h-11 rounded-xl bg-[#c59b27]/15 border border-[#c59b27]/60 flex items-center justify-center text-[#c59b27] mx-auto mb-2.5 shadow-lg shadow-black/60">
              <CheckCircle2 className="w-6 h-6" />
            </div>
            <h3 className="font-serif text-xl sm:text-2xl font-bold text-slate-100">
              {t('confirm_appointment_title', 'Arizani Tasdiqlash')}
            </h3>
            <p className="text-[11px] sm:text-xs text-slate-400 mt-1 font-light max-w-sm mx-auto">
              {t('confirm_appointment_subtitle', 'Iltimos, kiritilgan ma’lumotlarni tekshiring va arizani yuborishni tasdiqlang')}
            </p>
          </div>

          {/* Details Summary Card */}
          <div className="bg-[#0b1220] border border-[#1b2b46] rounded-xl p-4 sm:p-4.5 mb-4 space-y-3 text-xs">
            
            {/* Service & Lawyer */}
            <div className="flex items-start justify-between pb-3 border-b border-[#141f33]">
              <div className="flex items-center text-slate-300">
                <Briefcase className="w-4 h-4 mr-2 text-[#c59b27]" />
                <span className="font-medium">{t('service_and_lawyer', 'Xizmat va Advokat:')}</span>
              </div>
              <div className="text-right">
                <div className="font-semibold text-slate-100">{serviceTitle || 'Yuridik konsultatsiya'}</div>
                <div className="text-[11px] text-[#c59b27]">{lawyerName || 'Navbatchi advokat'}</div>
              </div>
            </div>

            {/* Date & Time */}
            <div className="flex items-center justify-between pb-3 border-b border-[#141f33]">
              <div className="flex items-center text-slate-300">
                <Calendar className="w-4 h-4 mr-2 text-[#c59b27]" />
                <span className="font-medium">{t('date_and_time', 'Sana va Vaqt:')}</span>
              </div>
              <div className="text-right font-semibold text-slate-100">
                {date} | {currentLang === 'uz' ? 'soat' : currentLang === 'ru' ? 'время' : 'time'} {time}
              </div>
            </div>

            {/* Client Info */}
            <div className="flex items-start justify-between pb-3 border-b border-[#141f33]">
              <div className="flex items-center text-slate-300">
                <User className="w-4 h-4 mr-2 text-[#c59b27]" />
                <span className="font-medium">{t('applicant', 'Ariza Beruvchi:')}</span>
              </div>
              <div className="text-right">
                <div className="font-semibold text-slate-100">{clientName}</div>
                <div className="font-mono text-slate-300">{clientPhone}</div>
                {(clientRegion || clientDistrict) && (
                  <div className="text-[10px] text-slate-400 flex items-center justify-end mt-0.5">
                    <MapPin className="w-3 h-3 mr-1 text-[#c59b27]" />
                    {clientDistrict ? `${clientDistrict}, ` : ''}{clientRegion}
                  </div>
                )}
              </div>
            </div>

            {/* Reason */}
            <div className="pt-1">
              <span className="text-slate-400 font-medium block mb-1">{t('reason_for_visit', 'Murojaat sababi:')}</span>
              <p className="text-slate-200 bg-[#060a12] p-2.5 rounded border border-[#141f33] italic font-light line-clamp-3">
                "{clientReason}"
              </p>
            </div>

          </div>

          {/* SMS & Notification Info Box */}
          <div className="p-3.5 rounded-lg bg-amber-500/10 border border-amber-500/30 text-amber-300/90 text-xs flex items-start space-x-2.5 mb-6">
            <AlertCircle className="w-4 h-4 shrink-0 mt-0.5 text-amber-400" />
            <span className="leading-relaxed">
              {t('sms_notice_hint', 'Tasdiqlash tugmasini bosganingizdan so‘ng arizangiz adminga kelib tushadi. Ma’muriyat arizani qabul qilgach yoki tasdiqlagach, telefoningizga SMS hamda shaxsiy profilingizdagi qo‘ng‘iroqchaga bildirishnoma yuboriladi.')}
            </span>
          </div>

          {/* Action Buttons */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="w-full py-3 px-4 rounded-lg border border-slate-700 hover:border-slate-500 text-xs font-semibold text-slate-300 hover:text-white transition-colors cursor-pointer disabled:opacity-50"
            >
              {t('cancel_btn', 'Bekor qilish')}
            </button>

            <button
              type="button"
              onClick={onConfirm}
              disabled={loading}
              className="w-full py-3 px-4 rounded-lg bg-gradient-to-r from-[#b38038] to-[#c59b27] hover:from-[#c59b27] hover:to-[#d4af37] text-white text-xs font-bold uppercase tracking-wider shadow-lg shadow-[#b38038]/30 transition-all flex items-center justify-center cursor-pointer disabled:opacity-50"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {t('submitting', 'Yuborilmoqda...')}
                </>
              ) : (
                t('confirm_btn_yes', 'HA, TASDIQLAYMAN')
              )}
            </button>
          </div>

        </div>
      </div>
    </div>
  );
}
